import { useEffect, useMemo, useState } from "react";
import { usePatientDocuments } from "pages/patient/PatientDocumentsPage/hooks/usePatientDocuments";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { SearchInput } from "components/ui/SearchInput";
import { Select } from "components/ui/Select/Select";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";
import { formatDocumentTypeLabel } from "utils/documentTypeIcons";

import s from "./AddDocumentsModal.module.css";

const ALL_TYPES = "ALL";
const DEFAULT_TYPE = "LETTER";

export const AddDocumentsModal = ({
  isOpen,
  onClose,
  patientId,
  initiallySelectedIds = [],
  onConfirm,
}) => {
  const { data: documentTypes = [], isLoading: typesLoading } =
    useDocumentTypes();

  const enabledTypes = useMemo(
    () => documentTypes.filter((dt) => !dt.disabled),
    [documentTypes]
  );

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(DEFAULT_TYPE);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(initiallySelectedIds.map(String)));
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Once types load, ensure the active filter resolves to a real type. Prefer
  // LETTER if available; otherwise fall back to the first enabled type.
  useEffect(() => {
    if (!enabledTypes.length) return;

    const hasCurrent =
      typeFilter === ALL_TYPES ||
      enabledTypes.some(
        (dt) => dt.name?.toUpperCase() === typeFilter?.toUpperCase()
      );

    if (hasCurrent) return;

    const letter = enabledTypes.find(
      (dt) => dt.name?.toUpperCase() === DEFAULT_TYPE
    );
    setTypeFilter((letter?.name || enabledTypes[0].name).toUpperCase());
  }, [enabledTypes, typeFilter]);

  const queryType = typeFilter === ALL_TYPES ? undefined : typeFilter;

  const { data: documents = [], isFetching: documentsLoading } =
    usePatientDocuments(patientId, queryType, {
      enabled: isOpen && !!patientId,
    });

  const filteredDocuments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return documents;

    return documents.filter((doc) => {
      const name = (doc.name || "").toLowerCase();
      const fileName = (doc.fileName || "").toLowerCase();
      return name.includes(term) || fileName.includes(term);
    });
  }, [documents, search]);

  const typeOptions = useMemo(
    () => [
      { label: "All Types", value: ALL_TYPES },
      ...enabledTypes.map((dt) => ({
        label: dt.label || formatDocumentTypeLabel(dt.name),
        value: dt.name.toUpperCase(),
      })),
    ],
    [enabledTypes]
  );

  const toggle = (id) => {
    const idStr = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }

      return next;
    });
  };

  const handleConfirm = () => {
    const selectedDocs = documents.filter((doc) =>
      selectedIds.has(String(doc.id))
    );
    onConfirm(selectedDocs);
    onClose();
  };

  const isLoading = typesLoading || documentsLoading;

  return (
    <Dialog open={isOpen} onClose={onClose} title="Add Documents" width={600}>
      <div className={s.filters}>
        <div className={s.typeField}>
          <Select
            options={typeOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            disabled={typesLoading || enabledTypes.length === 0}
            placeholder=""
            fullWidth
          />
        </div>
        <div className={s.searchField}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search documents..."
            variant="bordered"
          />
        </div>
      </div>

      <div className={s.list}>
        {isLoading ? (
          <p className={s.loading}>Loading documents...</p>
        ) : filteredDocuments.length === 0 ? (
          <p className={s.empty}>
            {documents.length === 0
              ? "No documents found for this type"
              : "No documents match your search"}
          </p>
        ) : (
          filteredDocuments.map((doc) => {
            const isSelected = selectedIds.has(String(doc.id));

            return (
              <div
                key={doc.id}
                className={`${s.item} ${isSelected ? s.itemSelected : ""}`}
                onClick={() => toggle(doc.id)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => toggle(doc.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className={s.itemBody}>
                  <div className={s.itemName}>
                    {doc.name || doc.fileName || "Untitled Document"}
                  </div>
                  <div className={s.itemMeta}>
                    {formatDocumentTypeLabel(doc.documentType) || "Document"}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className={s.footer}>
        <span className={s.count}>{selectedIds.size} selected</span>
        <div className={s.actions}>
          <Button variant="outline" size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="large" onClick={handleConfirm}>
            Attach {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
